/**
 * Shared internals for the card family — the five standalone cards from the
 * Figma "Interactive card" set (PickerCard, DetailCard, CompactCard, PromoCard,
 * OptionListCard) compose these sub-schemas, tokens, and pure helpers.
 *
 * Presentational and condition-agnostic — every card supplies copy, data, and
 * selected state from the host; nothing here encodes domain logic.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 *
 * Internal to the card family — not re-exported from the package barrel.
 */

import { z } from 'zod';
import { actionIdInputSchema } from '@galapagos/product-analytics';
import { ButtonState } from '../Button/Button';
import { colors } from '../../elements/colors';
import { typography } from '../../elements/typography';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { states } from '../../elements/states';
import { IconNameEnum } from '../Icon/Icon';

/** A circular avatar reference — image source with an initials fallback. */
export const cardAvatarSchema = z.object({
  imageUri: z.string().optional(),
  label: z.string().optional(),
});

/** A leading avatar/title/subtitle header row. */
export const cardHeaderSchema = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  avatar: cardAvatarSchema.optional(),
});

/** A detail line: text with an optional leading icon or avatar. */
export const cardDetailSchema = z.object({
  value: z.string(),
  iconName: IconNameEnum.optional(),
  avatar: cardAvatarSchema.optional(),
});

/** A label and caller-owned semantic identity for a call-to-action. */
export const cardActionSchema = z.object({
  actionId: actionIdInputSchema,
  label: z.string(),
  /** Interaction state for the rendered button (e.g. Loading while a host-owned commit is pending). */
  state: ButtonState.optional(),
});

/** A label call-to-action with an optional leading icon. */
export const cardTrailingActionSchema = z.object({
  label: z.string(),
  iconName: IconNameEnum.optional(),
});

/** A completion-status footer: a success badge (icon, defaults to a tick) + label. */
export const cardStatusSchema = z.object({
  iconName: IconNameEnum.optional(),
  label: z.string(),
});

/** A compact two-line chip item: a small caption over a larger label. */
export const cardChipItemSchema = z.object({
  id: z.string(),
  caption: z.string().optional(),
  label: z.string(),
  disabled: z.boolean().optional(),
});

/** A row item: an optional leading icon plus a label. */
export const cardRowItemSchema = z.object({
  id: z.string(),
  iconName: IconNameEnum.optional(),
  label: z.string(),
  disabled: z.boolean().optional(),
});

/** An ordered option: a label with an optional sublabel above it. */
export const cardOptionItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  sublabel: z.string().optional(),
});

/** A single-select group of items, with an optional accessible group label. */
const selectGroupSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    label: z.string().optional(),
    items: z.array(item),
    selectedId: z.string().optional(),
  });

export const cardChipGroupSchema = selectGroupSchema(cardChipItemSchema);
export const cardRowGroupSchema = selectGroupSchema(cardRowItemSchema);

export type CardAvatar = z.infer<typeof cardAvatarSchema>;
export type CardHeader = z.infer<typeof cardHeaderSchema>;
export type CardDetail = z.infer<typeof cardDetailSchema>;
export type CardAction = z.infer<typeof cardActionSchema>;
export type CardTrailingAction = z.infer<typeof cardTrailingActionSchema>;
export type CardStatus = z.infer<typeof cardStatusSchema>;
export type CardChipItem = z.infer<typeof cardChipItemSchema>;
export type CardRowItem = z.infer<typeof cardRowItemSchema>;
export type CardOptionItem = z.infer<typeof cardOptionItemSchema>;
export type CardChipGroup = z.infer<typeof cardChipGroupSchema>;
export type CardRowGroup = z.infer<typeof cardRowGroupSchema>;

/** Resolved colour palette for a card. */
export interface CardColors {
  surface: string;
  innerSurface: string;
  eyebrow: string;
  title: string;
  body: string;
  icon: string;
  rowSurface: string;
  badgeSurface: string;
  border: string;
}

/**
 * Light-surface palette. `background` overrides the surface; the border is a
 * divider-grey frame so full-bleed white items stay delineated on a white page.
 *
 * `border` overrides that frame. The divider grey is chosen for white and
 * near-white fills, where it delineates without being seen; on a saturated fill
 * it reads as a visible grey outline the design does not draw. A saturated
 * surface passes its own border here — matching the fill to erase the ring, or a
 * darker tone of it to keep one deliberately.
 */
export function resolveLightCardColors(
  surface: string,
  background?: string,
  border?: string,
): CardColors {
  return {
    surface: background ?? surface,
    innerSurface: colors.background.container,
    eyebrow: colors.text.accent,
    title: colors.text.primary,
    body: colors.text.primary,
    icon: colors.icon.primary,
    rowSurface: colors.background.primary,
    badgeSurface: colors.background.secondary,
    border: border ?? colors.stroke.divider,
  };
}

/**
 * Dark-surface palette. The surface is already distinct on any page, so the
 * border matches the surface and adds no visible frame.
 */
export function resolveDarkCardColors(background?: string): CardColors {
  return {
    surface: background ?? colors.background.dark,
    innerSurface: colors.background.dark,
    eyebrow: colors.text.highlightInverted,
    title: colors.text.inverted,
    body: colors.text.inverted,
    icon: colors.icon.inverted,
    rowSurface: colors.background.dark,
    badgeSurface: colors.button.inactive,
    border: background ?? colors.background.dark,
  };
}

/** WCAG relative luminance of an `#rrggbb` colour. */
export function relativeLuminance(hex: string): number {
  const m = hex.replace('#', '');
  const full =
    m.length === 3
      ? m
          .split('')
          .map((c) => c + c)
          .join('')
      : m;
  const channel = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const r = channel(parseInt(full.slice(0, 2), 16));
  const g = channel(parseInt(full.slice(2, 4), 16));
  const b = channel(parseInt(full.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio (1–21) between two `#rrggbb` colours. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Typography tokens per role. */
export const CARD_TYPOGRAPHY = {
  eyebrow: typography.c1Regular,
  title: typography.b1Bold,
  titleLarge: typography.h2Bold,
  body: typography.b2Regular,
  detail: typography.b1Bold,
  rowLabel: typography.b2Bold,
  chipCaption: typography.c1Regular,
  chipLabel: typography.h2Bold,
} as const;

/** Layout constants. */
export const CARD_RADIUS = radius.xl;
export const CARD_INNER_RADIUS = radius.xl;
export const CARD_ROW_RADIUS = radius.xl;
export const CARD_CHIP_RADIUS = radius.m;
export const CARD_PILL_RADIUS = radius.rounded;
export const CARD_PADDING = spacing.m;
export const CARD_GAP = spacing.m;
export const CARD_ROW_GAP = spacing.s;
export const CARD_AVATAR_SIZE = 48;
export const CARD_BADGE_SIZE = 44;
export const CARD_ICON_SIZE = 20;
/** Detail-row leading icon or avatar (Figma 16). */
export const CARD_DETAIL_GLYPH_SIZE = 16;
/**
 * Dismiss control: a 24px circle (Figma `p-xs` + 16 slot) holding a small
 * centered cross. The glyph is smaller than the slot because our `close` icon
 * fills its viewBox edge-to-edge, where Figma's "X small" is inset in the slot.
 */
export const CARD_DISMISS_BOX = 24;
export const CARD_DISMISS_SIZE = 12;
/** Minimum tappable target (ADR-025 Rule 5). */
export const CARD_MIN_TOUCH_TARGET = 44;
export const CARD_DISABLED_OPACITY = states.opacity.disabled;

/** Chip-group item dimensions (Figma node 2148:314). */
export const CARD_CHIP_WIDTH = 70;
export const CARD_CHIP_HEIGHT = 40;
/** Row-group item width and selection-indicator diameter. */
export const CARD_ROW_WIDTH = 160;
export const CARD_RADIO_SIZE = 24;
